import clsx from 'clsx';
import type { PaymentBreakdown } from '../../core/api/http-methods/get-user-transactions';
import { formatCurrency } from '../../utils/fiat-currency.util';
import { FIELD_LABEL } from './detail-classes';
import { BreakdownRow } from './detail-parts';

export type StoreBreakdown = Omit<PaymentBreakdown, 'refunded_amount'>;

/** A line that makes the bill add up to what was paid; `amount` is signed. */
export interface BreakdownAdjustment {
  label: string;
  amount: number;
}

/**
 * What a store was paid: its name, the bill's lines, and "Paid to {store}". Shared by the
 * transaction details page and the public receipt view; `children` (the order's items) sit
 * between the store's name and the subtotal, and an `adjustment` sits after tax, so the lines
 * add up to the total.
 */
export function StoreBreakdownRows({
  breakdown,
  currency,
  storeName,
  adjustment = null,
  children
}: {
  breakdown: StoreBreakdown;
  currency: string;
  storeName: string;
  adjustment?: BreakdownAdjustment | null;
  children?: React.ReactNode;
}) {
  return (
    <>
      <p className={clsx('mb-1', FIELD_LABEL)}>{storeName}</p>
      {children}
      <BreakdownRow
        label="Subtotal"
        value={formatCurrency(breakdown.subtotal, currency)}
      />
      {breakdown.discount > 0 && (
        <BreakdownRow
          label="Discount"
          value={`- ${formatCurrency(breakdown.discount, currency)}`}
          valueClassName="text-gray-700"
        />
      )}
      {breakdown.service_charge_rate != null &&
        breakdown.service_charge_amount != null && (
          <BreakdownRow
            label={`Service Charge (${breakdown.service_charge_rate}%)`}
            value={formatCurrency(breakdown.service_charge_amount, currency)}
          />
        )}
      {breakdown.tax_rate != null && breakdown.tax_amount != null && (
        <BreakdownRow
          label={`Tax (${breakdown.tax_rate}%)`}
          value={formatCurrency(breakdown.tax_amount, currency)}
        />
      )}
      {adjustment && (
        <BreakdownRow
          label={adjustment.label}
          value={
            adjustment.amount < 0
              ? `- ${formatCurrency(-adjustment.amount, currency)}`
              : formatCurrency(adjustment.amount, currency)
          }
        />
      )}
      <BreakdownRow
        bold
        label={`Paid to ${storeName}`}
        value={formatCurrency(breakdown.total, currency)}
      />
    </>
  );
}
